import { z } from "zod";
import { DateTimeWithOffset } from "@/lib/api/schemas/patterns";
import { Auth } from "../auth";
import type { ActionReturnType } from "../types";
import { request, validateInput } from "../request";
import { FileTypes } from "@/lib/api/schemas/shared";

class FilesEndpoint {
  public static readonly basePath = "files";
  public static readonly schemas = {
    list: {
      response: z.object({
        count: z.number(),
        items: z
          .object({
            id: z.number(),
            created_at: DateTimeWithOffset,
            category: FileTypes,
            step_id: z.number().nullable(),
            filename: z.string(),
            public_url: z.string().nullable(),
          })
          .array(),
      }),
    },
    read: {
      response: z.object({
        id: z.number(),
        created_at: DateTimeWithOffset,
        category: FileTypes,
        step_id: z.number().nullable(),
        filename: z.string(),
        public_url: z.string().nullable(),
      }),
    },
    update: {
      body: z.object({
        category: z.string(),
      }),
    },
  };

  constructor(private api: Auth) {}

  public list(): ActionReturnType<z.infer<typeof FilesEndpoint.schemas.list.response>> {
    return request({
      log: ["Files", "list"],
      method: "get",
      path: [FilesEndpoint.basePath],
      response: FilesEndpoint.schemas.list.response,
    });
  }

  public read(id: string): ActionReturnType<z.infer<typeof FilesEndpoint.schemas.read.response>> {
    return request({
      log: ["Files", "read"],
      method: "get",
      path: [FilesEndpoint.basePath, id],
      response: FilesEndpoint.schemas.read.response,
    });
  }

  public async update(id: string, body: z.infer<typeof FilesEndpoint.schemas.update.body>): ActionReturnType<undefined> {
    const bodyValidated = validateInput(FilesEndpoint.schemas.update.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Files", "update"],
      method: "put",
      path: [FilesEndpoint.basePath, id],
      json: bodyValidated.data,
    });
  }

  public delete(id: string): ActionReturnType<undefined> {
    return request({
      log: ["Files", "delete"],
      method: "delete",
      path: [FilesEndpoint.basePath, id],
    });
  }

  public download(id: string): ActionReturnType<undefined> {
    return request({
      log: ["Files", "download"],
      method: "get",
      path: [FilesEndpoint.basePath, id, "download"],
    });
  }
}

export { FilesEndpoint };
